import { ChevronLeft, SquarePen, Tv } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { useIsMobile } from "../hooks/use-mobile";
import { useIsAdmin, useIsMember } from "../lib/permissions";
import { useFactory } from "../store";
import { BrandMark } from "./brand";
import { NAV } from "./nav";
import { StatusDot } from "./status-badge";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { SidebarTrigger } from "./ui/sidebar";
import { Switch } from "./ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

function titleFor(pathname: string): string {
  if (pathname.startsWith("/inbox")) return "Notifications";
  if (pathname === "/issues/new") return "New issue";
  if (pathname.startsWith("/tasks/")) return "Work order";
  if (pathname.startsWith("/issues/")) return "Issue";
  if (pathname.startsWith("/teams/")) return "Team";
  if (pathname.startsWith("/repos/")) return "Repo";
  if (pathname.startsWith("/releases/")) return "Release";
  if (pathname.startsWith("/previews/")) return "Preview";
  const items = NAV.flatMap((g) => g.items);
  return items.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)))?.label ?? "Skynet";
}

/** A record's page, and the list a phone's back button returns to when there is no history. */
function parentOf(pathname: string): string | undefined {
  const match = /^\/(issues|tasks|teams|releases|previews)\/[^/]+$/.exec(pathname);
  return match ? `/${match[1]}` : undefined;
}

/** Whether the window has scrolled past `offset`, for the phone header's collapsing title. */
function useScrolledPast(offset: number): boolean {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const onScroll = () => setPast(window.scrollY > offset);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [offset]);
  return past;
}

export function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { realtime, state } = useFactory();
  const admin = useIsAdmin();
  const member = useIsMember();
  const isMobile = useIsMobile();
  const scrolled = useScrolledPast(44);
  const parent = parentOf(location.pathname);
  const live = realtime === "connected";
  const label = live
    ? "Live"
    : realtime === "disabled"
      ? "Polling"
      : realtime === "error"
        ? "Offline"
        : "Connecting";
  const tone = live ? "emerald" : realtime === "disabled" ? "sky" : realtime === "error" ? "red" : "amber";
  // A phone's list pages carry their own large title, so the bar only shows it once that scrolls away.
  const titleHidden = isMobile && !parent && !scrolled;

  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(parent ?? "/");
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-10 box-content flex h-12 shrink-0 items-center gap-2 border-b bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur-xl transition-[border-color] duration-(--motion-dur) md:rounded-t-xl",
        titleHidden && "border-transparent",
      )}
    >
      {/* The title starts on the content gutter, so it lines up with the page header below it. */}
      <div className="flex w-full items-center gap-2 px-gutter">
        {isMobile ? (
          parent ? (
            <Button variant="ghost" size="icon-sm" className="-ml-2" aria-label="Back" onClick={goBack}>
              <ChevronLeft className="size-5.5" />
            </Button>
          ) : (
            <BrandMark className="size-7 rounded-md" />
          )
        ) : (
          <>
            <SidebarTrigger className="-ml-1.5 text-muted-foreground" />
            <Separator orientation="vertical" className="mx-1.5 data-[orientation=vertical]:h-4" />
          </>
        )}
        <h1
          className={cn(
            "truncate text-sm font-medium transition-[opacity,transform] duration-(--motion-dur) max-md:text-[15px] max-md:font-semibold",
            titleHidden && "translate-y-1 opacity-0",
          )}
        >
          {titleFor(location.pathname)}
        </h1>
        <div className="ml-auto flex items-center gap-4 max-md:gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center gap-2 text-xs text-muted-foreground">
                <StatusDot tone={tone} pulse={live} />
                <span className="max-sm:sr-only">{label}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {state
                ? `Updated ${new Date(state.generatedAt).toLocaleTimeString()}`
                : admin
                  ? "Waiting for data"
                  : "Issue updates"}
            </TooltipContent>
          </Tooltip>
          {isMobile ? (
            member &&
            location.pathname !== "/issues/new" && (
              <Button
                variant="ghost"
                size="icon-sm"
                className="-mr-2 ml-1"
                aria-label="New issue"
                onClick={() => navigate("/issues/new")}
              >
                <SquarePen className="size-5" />
              </Button>
            )
          ) : (
            <>
              <Separator orientation="vertical" className="data-[orientation=vertical]:h-4" />
              <Label className="cursor-pointer gap-2 text-xs font-medium text-muted-foreground">
                <Tv className="size-3.5" />
                TV mode
                <Switch
                  checked={false}
                  onCheckedChange={() =>
                    navigate("/tv", { state: { from: location.pathname + location.search } })
                  }
                />
              </Label>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
